import { Helmet } from "react-helmet-async";
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogIn, Mail, Lock, ArrowRight } from 'lucide-react';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const { login, verify2fa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tfaState, setTfaState] = useState(null); // { tfa_token, tfa_method, email }
  const [tfaCode, setTfaCode] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.requires_2fa) {
        setTfaState(result);
        setLoading(false);
        return;
      }
      navigate(result.role === 'practitioner' ? '/my-studio' : '/dashboard', { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2fa = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await verify2fa(tfaState.tfa_token, tfaCode);
      navigate(user.role === 'practitioner' ? '/my-studio' : '/dashboard', { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex flex-col">
      <Helmet><title>Login | Go Freestyle Studios</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <div className="px-6 py-6">
        <Link to="/" className="flex items-center" data-testid="brand-link">
          <img src="/logo.webp" alt="Go Freestyle Studios" className="h-[52px]" style={{filter:'contrast(1.15) brightness(0.95)'}} />
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-sm">

          {/* 2FA Verification */}
          {tfaState ? (
            <div className="animate-fade-in" data-testid="tfa-verify-section">
              <div className="mb-10">
                <h1 className="font-heading text-3xl sm:text-4xl font-medium text-[#1A1A1A] tracking-tight">Verification</h1>
                <p className="mt-2 text-sm text-[#5C5C55]">
                  {tfaState.tfa_method === 'email'
                    ? `Enter the 6-digit code sent to ${tfaState.email}`
                    : 'Enter the code from your authenticator app'}
                </p>
              </div>
              {error && <div className="mb-6 p-4 bg-[#F5E6E6] text-[#8A3D3D] text-sm rounded-xl" data-testid="tfa-error">{error}</div>}
              <form onSubmit={handleVerify2fa} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="code" className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Verification Code</Label>
                  <Input id="code" value={tfaCode} onChange={e => setTfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000" required maxLength={6} autoFocus
                    className="bg-[#F2F0EB] border-transparent rounded-xl px-4 py-3 text-2xl text-center tracking-[0.5em] font-heading focus:bg-white focus:border-[#3A4035] focus:ring-1 focus:ring-[#3A4035] h-14 placeholder:text-[#D8DBD3]"
                    data-testid="tfa-code-input" />
                </div>
                <Button type="submit" disabled={loading || tfaCode.length < 6}
                  className="w-full bg-[#3A4035] text-white rounded-full px-6 py-3 h-12 font-medium hover:bg-[#2B3027] transition-colors"
                  data-testid="tfa-submit-button">
                  {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Verify'}
                </Button>
              </form>
              <button onClick={() => { setTfaState(null); setTfaCode(''); setError(''); }} className="mt-4 text-sm text-[#5C5C55] hover:underline w-full text-center" data-testid="tfa-back">
                Back to login
              </button>
            </div>
          ) : (
          <>
          <div className="mb-10">
            <h1 className="font-heading text-3xl sm:text-4xl font-medium text-[#1A1A1A] tracking-tight" data-testid="login-heading">
              Welcome back
            </h1>
            <p className="mt-2 text-sm text-[#5C5C55]">Sign in to access your studio</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-[#F5E6E6] text-[#8A3D3D] text-sm rounded-xl" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Email</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8B85]" strokeWidth={1.5} />
                <Input
                  id="email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com" required
                  className="pl-11 bg-[#F2F0EB] border-transparent rounded-xl px-4 py-3 text-base focus:bg-white focus:border-[#3A4035] focus:ring-1 focus:ring-[#3A4035] h-12 placeholder:text-[#8A8B85]"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8B85]" strokeWidth={1.5} />
                <Input
                  id="password" type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password" required
                  className="pl-11 bg-[#F2F0EB] border-transparent rounded-xl px-4 py-3 text-base focus:bg-white focus:border-[#3A4035] focus:ring-1 focus:ring-[#3A4035] h-12 placeholder:text-[#8A8B85]"
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <Button
              type="submit" disabled={loading}
              className="w-full bg-[#3A4035] text-white rounded-full px-6 py-3 h-12 font-medium hover:bg-[#2B3027] transition-colors"
              data-testid="login-submit-button"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>Sign in <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} /></>
              )}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-4">
            <div className="flex-1 h-px bg-[#E5E2DC]" />
            <span className="text-xs text-[#8A8B85] uppercase tracking-wider">or</span>
            <div className="flex-1 h-px bg-[#E5E2DC]" />
          </div>

          <Button
            variant="outline" onClick={handleGoogleLogin}
            className="w-full border border-[#E5E2DC] text-[#1A1A1A] rounded-full px-6 py-3 h-12 font-medium hover:bg-[#F2F0EB] transition-colors"
            data-testid="google-login-button"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </Button>

          <p className="mt-8 text-center text-sm text-[#5C5C55]">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#3A4035] font-medium hover:underline" data-testid="register-link">
              Get in touch
            </Link>
          </p>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
